import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createOrganisation, queryOrganisations } from '@/features/organisations/data';
import { useOrganisationFilter } from '@/features/organisations/providers/organisationHooks';
import { useProjectFilter } from '@/features/projects';
import { createProject as createProjectRequest, queryProjects } from '@/features/projects/data';
import { useNotifications } from '@/providers/notificationHooks';
import { buildNamespacedStorageKey } from '@/shared/data/cacheNamespace';
import { gridPaths, invalidateCurrentUserRoles, invalidateGridQuery, queryKeys } from './queryKeys';
import { useInvalidateQueries } from './useInvalidateQueries';
import type { AuthSession } from '@/auth/types';
import type { OrganisationDto, ProjectDto } from '@flowqa/shared';

type UseBootstrapDataArgs = { session: AuthSession | null };
type OrganisationOption = Pick<OrganisationDto, 'id' | 'name' | 'slug'>;
type ProjectOption = Pick<ProjectDto, 'id' | 'name' | 'slug' | 'organisationId'>;

const ORG_OPTIONS_CACHE_KEY = 'flowqa-org-options-cache';
const PROJECT_OPTIONS_CACHE_KEY = 'flowqa-project-options-cache';
const ORG_FILTER_KEY = 'flowqa-organisation-filter';
const PROJECT_FILTER_KEY = 'flowqa-project-filter';

const orgOptionsCacheKey = (): string => buildNamespacedStorageKey(ORG_OPTIONS_CACHE_KEY);
const projectOptionsCacheKey = (): string => buildNamespacedStorageKey(PROJECT_OPTIONS_CACHE_KEY);
const orgFilterCacheKey = (): string => buildNamespacedStorageKey(ORG_FILTER_KEY);
const projectFilterCacheKey = (): string => buildNamespacedStorageKey(PROJECT_FILTER_KEY);

const writeCachedOrganisations = (organisations: OrganisationOption[]): void => {
  if (typeof window === 'undefined') return;
  window.localStorage.setItem(orgOptionsCacheKey(), JSON.stringify(organisations));
};

const toProjectOption = (project: ProjectDto): ProjectOption => ({
  id: project.id,
  name: project.name,
  slug: project.slug,
  organisationId: project.organisationId,
});

const upsertProjectById = (items: ProjectDto[], nextItem: ProjectDto): ProjectDto[] => {
  const existingIndex = items.findIndex((item) => item.id === nextItem.id);
  if (existingIndex === -1) {
    return [...items, nextItem];
  }
  const next = [...items];
  next[existingIndex] = nextItem;
  return next;
};

const upsertProjectOptionById = (
  items: ProjectOption[],
  nextItem: ProjectOption,
): ProjectOption[] => {
  const existingIndex = items.findIndex((item) => item.id === nextItem.id);
  if (existingIndex === -1) {
    return [...items, nextItem];
  }
  const next = [...items];
  next[existingIndex] = nextItem;
  return next;
};

const readProjectOptionsCacheMap = (): Record<string, ProjectOption[]> => {
  if (typeof window === 'undefined') return {};
  const raw = window.localStorage.getItem(projectOptionsCacheKey());
  if (!raw) return {};
  try {
    const parsed = JSON.parse(raw) as unknown;
    if (typeof parsed !== 'object' || parsed === null) return {};
    const entries = Object.entries(parsed);
    const validEntries = entries
      .map(([key, value]): [string, ProjectOption[]] | null => {
        if (!Array.isArray(value)) return null;
        const projects = value.filter(
          (item): item is ProjectOption =>
            typeof item === 'object' &&
            item !== null &&
            typeof (item as { id?: unknown }).id === 'string' &&
            typeof (item as { name?: unknown }).name === 'string' &&
            typeof (item as { slug?: unknown }).slug === 'string' &&
            ((item as { organisationId?: unknown }).organisationId === null ||
              typeof (item as { organisationId?: unknown }).organisationId === 'string'),
        );
        return [key, projects];
      })
      .filter((entry): entry is [string, ProjectOption[]] => entry !== null);
    return Object.fromEntries(validEntries);
  } catch {
    return {};
  }
};

const getProjectCacheKey = (organisationId: string | null): string => organisationId ?? 'personal';

const writeCachedProjectOptions = (
  organisationId: string | null,
  projects: ProjectOption[],
): void => {
  if (typeof window === 'undefined') return;
  const map = readProjectOptionsCacheMap();
  map[getProjectCacheKey(organisationId)] = projects;
  window.localStorage.setItem(projectOptionsCacheKey(), JSON.stringify(map));
};

export const useBootstrapData = ({
  session,
}: UseBootstrapDataArgs): {
  projectsLoaded: boolean;
  projects: ProjectDto[];
  organisations: Array<Pick<OrganisationDto, 'id' | 'name' | 'slug'>>;
  visibleProjects: ProjectDto[];
  visibleProjectOptions: Array<Pick<ProjectDto, 'id' | 'name' | 'slug'>>;
  createProject: (
    input: { name: string },
    options?: { setAsCurrent?: boolean },
  ) => Promise<ProjectDto | null>;
  createOrg: (name: string) => Promise<OrganisationDto | null>;
} => {
  const { notify } = useNotifications();
  const { selectedOrganisationId, setSelectedOrganisationId } = useOrganisationFilter();
  const { selectedProjectId, setSelectedProjectId } = useProjectFilter();
  const [projects, setProjects] = useState<ProjectDto[]>([]);
  const [cachedProjectOptions, setCachedProjectOptions] = useState<ProjectOption[]>([]);
  const [organisations, setOrganisations] = useState<OrganisationOption[]>([]);
  const [hasLoadedProjects, setHasLoadedProjects] = useState(false);
  const loadSeq = useRef(0);
  const lastUserIdRef = useRef<string | null>(null);
  const invalidateQueries = useInvalidateQueries();
  const queryClient = useQueryClient();

  useEffect(() => {
    const nextUserId = session?.userId ?? null;
    if (lastUserIdRef.current === nextUserId) return;
    lastUserIdRef.current = nextUserId;
    setProjects([]);
    setOrganisations([]);
    setCachedProjectOptions([]);
    setHasLoadedProjects(false);
    setSelectedOrganisationId(null);
    setSelectedProjectId(null);
  }, [session?.userId, setSelectedOrganisationId, setSelectedProjectId]);

  useEffect(() => {
    const handleOrgCreated = (event: Event): void => {
      const detail = (event as CustomEvent<OrganisationDto>).detail;
      if (!detail.id) return;
      setOrganisations((prev) => {
        const next = prev.some((org) => org.id === detail.id)
          ? prev
          : [...prev, { id: detail.id, name: detail.name, slug: detail.slug }];
        writeCachedOrganisations(next);
        return next;
      });
    };
    const handleOrgUpdated = (event: Event): void => {
      const detail = (event as CustomEvent<{ id: string; name: string }>).detail;
      if (!detail.id) return;
      setOrganisations((prev) => {
        const next = prev.map((org) =>
          org.id === detail.id ? { ...org, name: detail.name } : org,
        );
        writeCachedOrganisations(next);
        return next;
      });
    };
    window.addEventListener('flowqa-org-created', handleOrgCreated);
    window.addEventListener('flowqa-org-updated', handleOrgUpdated);
    return () => {
      window.removeEventListener('flowqa-org-created', handleOrgCreated);
      window.removeEventListener('flowqa-org-updated', handleOrgUpdated);
    };
  }, []);

  useEffect(() => {
    const handleProjectCreated = (event: Event): void => {
      const detail = (event as CustomEvent<ProjectDto>).detail;
      if (!detail.id) return;
      setProjects((prev) =>
        prev.some((proj) => proj.id === detail.id) ? prev : [...prev, detail],
      );
      if (detail.organisationId !== selectedOrganisationId) return;
      setCachedProjectOptions((prev) => {
        const next = prev.some((project) => project.id === detail.id)
          ? prev
          : [...prev, toProjectOption(detail)];
        writeCachedProjectOptions(selectedOrganisationId, next);
        return next;
      });
    };
    window.addEventListener('flowqa-project-created', handleProjectCreated);
    return () => {
      window.removeEventListener('flowqa-project-created', handleProjectCreated);
    };
  }, [selectedOrganisationId]);

  useEffect(() => {
    const handleProjectUpdated = (event: Event): void => {
      const detail = (event as CustomEvent<ProjectDto>).detail;
      if (!detail.id) return;
      setProjects((prev) =>
        prev.map((project) => (project.id === detail.id ? { ...project, ...detail } : project)),
      );
      if (detail.organisationId !== selectedOrganisationId) return;
      setCachedProjectOptions((prev) => {
        const next = prev.map((project) =>
          project.id === detail.id ? toProjectOption(detail) : project,
        );
        writeCachedProjectOptions(selectedOrganisationId, next);
        return next;
      });
    };
    window.addEventListener('flowqa-project-updated', handleProjectUpdated);
    return () => {
      window.removeEventListener('flowqa-project-updated', handleProjectUpdated);
    };
  }, [selectedOrganisationId]);

  useEffect(() => {
    const handleProjectRemoved = (event: Event): void => {
      const detail = (event as CustomEvent<{ id: string }>).detail;
      if (!detail.id) return;
      setProjects((prev) => prev.filter((project) => project.id !== detail.id));
      setCachedProjectOptions((prev) => {
        const next = prev.filter((project) => project.id !== detail.id);
        writeCachedProjectOptions(selectedOrganisationId, next);
        return next;
      });
      if (selectedProjectId === detail.id) {
        setSelectedProjectId(null);
      }
    };
    window.addEventListener('flowqa-project-removed', handleProjectRemoved);
    return () => {
      window.removeEventListener('flowqa-project-removed', handleProjectRemoved);
    };
  }, [selectedOrganisationId, selectedProjectId, setSelectedProjectId]);

  useEffect(() => {
    const handleOrgRemoved = (event: Event): void => {
      const detail = (event as CustomEvent<{ reason?: string }>).detail;
      if (detail.reason === 'deleted_by_user') {
        return;
      }
      notify({ message: 'You no longer have access to that organisation', tone: 'orange' });
    };
    window.addEventListener('flowqa-org-removed', handleOrgRemoved);
    return () => {
      window.removeEventListener('flowqa-org-removed', handleOrgRemoved);
    };
  }, [notify]);

  useEffect(() => {
    const loadProjects = async (): Promise<void> => {
      const runId = ++loadSeq.current;
      setHasLoadedProjects(false);
      if (!session?.userId) {
        setProjects([]);
        setOrganisations([]);
        setCachedProjectOptions([]);
        setHasLoadedProjects(true);
        if (typeof window !== 'undefined') {
          window.localStorage.removeItem(orgOptionsCacheKey());
          window.localStorage.removeItem(projectOptionsCacheKey());
          window.localStorage.removeItem(orgFilterCacheKey());
          window.localStorage.removeItem(projectFilterCacheKey());
        }
        return;
      }
      try {
        const [orgs, projectsResult] = await Promise.all([
          queryOrganisations(),
          queryProjects(null),
        ]);
        if (runId !== loadSeq.current) return;
        setOrganisations(orgs.items);
        writeCachedOrganisations(orgs.items);
        setProjects(projectsResult.items);
        const nextProjectOptions = projectsResult.items.map(toProjectOption);
        setCachedProjectOptions(nextProjectOptions);
        writeCachedProjectOptions(null, nextProjectOptions);
        queryClient.setQueryData(queryKeys.organisations(session.userId), orgs.items);
        queryClient.setQueryData(queryKeys.projects(null), projectsResult);
        setHasLoadedProjects(true);
      } catch {
        if (runId !== loadSeq.current) return;
        // Never keep prior in-memory lists on load failure; prevents stale cross-user render.
        setProjects([]);
        setOrganisations([]);
        setCachedProjectOptions([]);
        setHasLoadedProjects(true);
      }
    };
    void loadProjects();
  }, [queryClient, session?.userId]);

  const createProject = useCallback(
    async (
      { name }: { name: string },
      options?: { setAsCurrent?: boolean },
    ): Promise<ProjectDto | null> => {
      if (!session?.userId) return null;
      const created = await createProjectRequest({ name }, undefined, selectedOrganisationId);
      setProjects((prev) => upsertProjectById(prev, created));
      if (created.organisationId === selectedOrganisationId) {
        setCachedProjectOptions((prev) => {
          const next = upsertProjectOptionById(prev, toProjectOption(created));
          writeCachedProjectOptions(selectedOrganisationId, next);
          return next;
        });
      }
      setHasLoadedProjects(true);
      if (options?.setAsCurrent !== false) {
        setSelectedProjectId(created.id);
      }
      notify({ message: 'Project created', tone: 'green' });
      await invalidateQueries([
        invalidateGridQuery(gridPaths.projects),
        invalidateCurrentUserRoles(),
      ]);
      return created;
    },
    [invalidateQueries, notify, selectedOrganisationId, session?.userId, setSelectedProjectId],
  );

  const createOrg = useCallback(
    async (name: string): Promise<OrganisationDto | null> => {
      if (!session?.userId) return null;
      const created = await createOrganisation({ name });
      notify({ message: 'Organisation created', tone: 'green' });
      const orgs = await queryOrganisations();
      setOrganisations(orgs.items);
      writeCachedOrganisations(orgs.items);
      setSelectedOrganisationId(created.id);
      setSelectedProjectId(null);
      await invalidateQueries([
        invalidateGridQuery(gridPaths.organisations),
        invalidateGridQuery(gridPaths.projects),
        invalidateGridQuery(gridPaths.users),
        invalidateCurrentUserRoles(),
      ]);
      return created;
    },
    [invalidateQueries, notify, session?.userId, setSelectedOrganisationId, setSelectedProjectId],
  );

  const visibleProjects = useMemo(() => {
    if (selectedOrganisationId === null) {
      return projects;
    }
    return projects.filter((p) => p.organisationId === selectedOrganisationId);
  }, [projects, selectedOrganisationId]);

  const visibleProjectOptions = useMemo(() => {
    const currentOptions = visibleProjects.map((project) => ({
      id: project.id,
      name: project.name,
      slug: project.slug,
    }));
    if (currentOptions.length > 0 || hasLoadedProjects) {
      return currentOptions;
    }
    return cachedProjectOptions.map((project) => ({
      id: project.id,
      name: project.name,
      slug: project.slug,
    }));
  }, [cachedProjectOptions, hasLoadedProjects, visibleProjects]);

  return {
    projectsLoaded: hasLoadedProjects,
    projects,
    organisations,
    visibleProjects,
    visibleProjectOptions,
    createProject,
    createOrg,
  };
};
